import { useEffect, useRef, useCallback } from 'react';
import { refreshSessionToken } from '../utils/api';

const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // Refresh every 6 hours

/**
 * Hook that periodically refreshes the session token to keep the Redis
 * accessSecret alive and prevent premature session expiry.
 *
 * The actual request lives in utils/api.ts (refreshSessionToken) rather than here.
 * This hook used to issue its own hand-rolled refresh, which caused the logouts it
 * was meant to prevent:
 *
 * - it had no single-flight guard, so two open tabs both firing their 30-second
 *   post-mount refresh raced each other
 * - it had no retry, so one failed request was fatal
 * - it called onSessionLost() on any 401/403, so a momentary blip - or losing that
 *   race - ended the session outright
 *
 * Going through the shared helper means it shares the interceptor's in-flight guard
 * and its network-error tolerance, and only a genuine server-side rejection counts
 * as a lost session.
 */
const useSessionRefresh = (
  _userType: 'Administrator' | 'Merchant',
  onSessionLost?: () => void,
) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshSession = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    const accessSecret = localStorage.getItem('accessSecret');

    if (!accessToken || !accessSecret) return;

    const outcome = await refreshSessionToken();

    // "network_error" means the request never completed - that says nothing about
    // whether the session is still valid, so leave it alone and try again next tick.
    if (outcome === 'auth_failed') {
      onSessionLost?.();
    }
  }, [onSessionLost]);

  useEffect(() => {
    // Initial refresh shortly after mount to reset the Redis TTL.
    const initialTimeout = setTimeout(refreshSession, 30_000);

    intervalRef.current = setInterval(refreshSession, REFRESH_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refreshSession]);

  return { refreshSession };
};

export default useSessionRefresh;
